import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from '../../config/axiosClient';
import Config from "../../config/Config.js";

const SearchDropdown = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);

  // Click outside to close
  const handleClickOutside = useCallback((e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setOpen(false);
    }
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") setOpen(false);
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleClickOutside, handleKeyDown]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const fetchResults = useCallback((q) => {
    if (q.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${Config().baseUrl}?search=${encodeURIComponent(q)}&limit=5`)
      .then((res) => {
        const data = res.data;
        const items = Array.isArray(data) ? data : data.results || [];
        setResults(items.slice(0, 5));
        setOpen(items.length > 0 || q.length >= 2);
      })
      .catch(() => {
        setResults([]);
        setOpen(q.length >= 2);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setActiveIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchResults(val), 300);
  };

  const handleInputKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleResultClick(results[activeIndex].id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (activeIndex >= 0 && results[activeIndex]) {
      handleResultClick(results[activeIndex].id);
      return;
    }
    setOpen(false);
    if (query.trim()) {
      window.location = `/search/?search=${query.trim()}`;
    }
  };

  const handleResultClick = (id) => {
    setOpen(false);
    window.location = `/projects/${id}`;
  };

  return (
    <div className="search-dropdown" ref={dropdownRef}>
      <form onSubmit={handleSubmit} className="d-flex flex-grow-1">
        <input
          value={query}
          onChange={handleChange}
          onKeyDown={handleInputKeyDown}
          className="form-control me-3"
          type="search"
          placeholder="Search"
          aria-label="Search"
        />
        <button className="btn btn-teal" type="submit">Search</button>
      </form>

      {open && (
        <div className="search-dropdown-panel">
          {loading && results.length === 0 && (
            <div className="search-dropdown-empty">Searching…</div>
          )}
          {!loading && results.length === 0 && query.length >= 2 && (
            <div className="search-dropdown-empty">No results</div>
          )}
          {results.map((project, idx) => (
            <button
              key={project.id}
              className={`search-dropdown-item${idx === activeIndex ? " active" : ""}`}
              onClick={() => handleResultClick(project.id)}
              onMouseEnter={() => setActiveIndex(idx)}
              type="button"
            >
              <span className="search-dropdown-title">{project.title}</span>
              {project.subtitle && (
                <span className="search-dropdown-subtitle">{project.subtitle}</span>
              )}
              {(project.technology || project.skill) && (
                <span className="search-dropdown-meta">
                  {[project.technology, project.skill].filter(Boolean).join(" | ")}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
